/**
 * @typedef {import('../../../core/services/quickfind.service').Action} Action
 * @typedef {import('../../../core/services/quickfind.service').Item} Item
 * @typedef {import('../../../core/services/quickfind.service').Result} Result
 */

import template from './quickfind.modal.component.html';


/**
 * Track the last-used-pattern for autocompletion later
 *
 * @type {string}
 */
let lastPattern = '';

/* @ngInject */
function quickFindController(
  $sce,
  $scope,
  $state,
  growl,
  QuickFindService,
) {
  const vm = this;
  _.assign(vm, QuickFindService.config);
  vm.results = [];
  vm.pattern = '';
  vm.focusIdx = 1;
  vm.modDown = false;


  vm.startCase    = _.startCase;

  vm.clearFocus   = clearFocus;
  vm.getLabelText = getLabelText;
  vm.hover        = handleMouseOver;
  vm.inRange      = inRange;
  vm.keyPress     = handleKeyPress;
  vm.selectResult = selectResult;
  vm.cogPress     = handleCogPress;

  vm.confirm = result => this.close({ $value: result });
  vm.cancel = result => this.dismiss({ $value: result });

  (function init() {
    const keyIsAlt = key => key === 'Alt';
    const setModState = (status) => {
      vm.modDown = status;
      $scope.$apply();
    };
    window.onkeydown = ({ key }) => {
      keyIsAlt(key) && setModState(true);
    };
    window.onkeyup = ({ key }) => {
      keyIsAlt(key) && setModState(false);
    };

    _.assign(vm, { lastPattern });
  })();
  $scope.$on('$destroy', () => {
    window.onkeydown = undefined;
    window.onkeyup = undefined;
  });


  function handleKeyPress(event) {
    const { key } = event;
    switch (key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        handleKeyNav(key);
        return false;
      case 'Enter':
        event.preventDefault();
        _.isEmpty(vm.results) || selectResult();
        return false;
      case 'Alt':
      // Ignore; handled in init
        return true;
      case 'Tab':
        event.preventDefault();
        if (!vm.pattern && vm.lastPattern) {
          vm.pattern = vm.lastPattern;
        }
        return false;
      default:
      // If the user is typing something else, return them to the input
        clearFocus();
        return true;
    }
  }

  function handleKeyNav(key) {
    const idxDelta = { ArrowDown: 1, ArrowUp: -1 }[key];
    const targetIdx = vm.focusIdx + idxDelta;

    const overflowing = targetIdx > vm.results.length;
    const underflowing = targetIdx < 1;
    const newIdx = overflowing ?
      1 :
      underflowing ?
        vm.results.length :
        targetIdx;

    setFocus(newIdx);
    document
      .getElementById(newIdx)
      .scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function setFocus(idx) {
    vm.focusIdx = idx;
  }
  function clearFocus() {
    setFocus(1);
  }


  /**
   * Generic alt actions
   *
   * @type {Action}
   * Rather than supplying an alt object, one can simply inject the name of a preset as an item's `alt` property
   */
  vm.altPresets = {
    // Default alt action for any result with an ID
    copy: {
      text: id => $sce.trustAsHtml(`Copy ID ${id} <span class="fad fa-copy"></span>`),
      do: (item) => {
        navigator.clipboard.writeText(item.id);
        growl.info(item.id, { title: `${item.name} copied:` });
      }
    },
    // Null action for overriding the default
    none: {
      text: ' ', // A single space character mitigates modal growth/shrinkages when pressing mod key
      do: _.noop
    }

  };

  /**
   * Calls the appropriate action for a given result:
   * A custom/alternate action if the result has one *and* the user is holding `Alt`
   * Otherwise, default navigation to the result's landing page will occur
   *
   * @param {Result} arg
   * @param {Item} arg.item
   * @returns {undefined}
   */
  function selectResult({ item } = vm.results[vm.focusIdx - 1]) {
    if (item.id && !item.alt) {
      item.alt = 'copy';
    }
    const altActionReady = item.alt && vm.modDown;
    const usingAltPreset = _.isString(item.alt);
    const selectAction =
      altActionReady ?
        usingAltPreset ?
          vm.altPresets[item.alt] || { do: () => console.error(`Bad action provided ${item.alt}`) } :
          item.alt :
        { do: ({ route, id, params = {} }) => $state.go(route, { ...params, id }) };
    vm.close(selectAction.do(item));
    lastPattern = vm.pattern;
  }

  function handleMouseOver({ toElement: hoverSource }) {
    const findResultRootElementIndex = element =>
      // If hover event is from children, we need to look for its parent instead
      element.tabIndex === -1 ?
        findResultRootElementIndex(element.parentElement) :
        element.tabIndex
    ;
    // TODO: Highlight without changing focus. As is… it's an annoying UX instead of convenient
    setFocus(findResultRootElementIndex(hoverSource));
    return false;
  }

  function handleCogPress() {
    vm.close($state.go('user profile', { '#': 'qf-settings' }));
  }

  /**
   * Check if a given number (index) is contained within any of the match indices within a {Result}
   *
   * @param {number} index
   * @param {Result} result
   * @returns {boolean}
   */
  function inRange(index, result) {
    return _.some(result.matches, ({ indices }) =>
      _.some(indices, ([start, end]) => _.inRange(index, start, end + 1))
    );
  }

  function getLabelText({ item: { alt = {}, id, label, route } }) {
    return vm.modDown ?
      alt.text || _.get(vm.altPresets, [alt, 'text'], id ? vm.altPresets.copy.text(id) : vm.altPresets.none.text) :
      id ? _.startCase(label || route) : vm.altPresets.none.text
    ;
  }


  const FIND_DELAY = 150;
  $scope.$watch(
    '$modal.pattern', // TODO: Pattern is a bad name that was used only because Fuze does too
    // Fast-typers otherwise suffer a laggy experience due to each char input triggering a digest
    _.debounce((query) => {
      if (!query) {
        return;
      }
      const filters = {
        db: 'dashboard',
        eq: 'equipment',
        grp: 'group',
        org: 'organization',
        site: 'site',
      };
        // TODO: Change template to allow new length
      const filtering = new RegExp(`^(${_.keys(filters).join('|')}):\\s?(.*)`, 'i');
      const [, filter, terms] = query.match(filtering) || [];
      const results = filter && terms ?
        QuickFindService
          .find(terms)
          .filter(({ item }) => item.label?.toLowerCase() === filters[filter] || item.route.includes(filters[filter])) :
        QuickFindService
          .find(filter ? terms : query);
      const shouldShowDummyResult = _.isEmpty(results) && (filter ? terms : query);
      vm.results = shouldShowDummyResult ?
        // TODO: Potentially entity is disabled but user forgot. We could poke them when they try to use that filter
        [{ item: { name: `No ${filters[filter] ?? 'results'} found` } }] :
        results.slice(0, vm.MAX_PATTERN_LENGTH / 2);

      $scope.$apply();
    }, FIND_DELAY)
  );
}


export default {
  name: 'quickfindModal',
  controller: quickFindController,
  controllerAs: '$modal',
  template,
  bindings: {
    resolve: '<',
    close: '&',
    dismiss: '&'
  }
};
